import type { CSSProperties } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BodyS,
  BrandM,
  BrandS,
  BrandXL,
} from '@sorare/core/src/atoms/typography';
import { Bezel } from '@sorare/core/src/atoms/ui/bezels/Bezel';

import baseShape from './assets/baseShape.webp';
import cards from './assets/cards.webp';
import phone from './assets/phone.webp';
import sideSvg from './assets/side.svg';

const Content = styled(Vertical)`
  margin: auto 0;
`;

const RewardsWrapper = styled(Horizontal)`
  width: 100%;
  border-radius: 16px;
  overflow: hidden;
  align-items: stretch;
`;

const RewardsBanner = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  background: url(${baseShape}) no-repeat center center / cover;
  padding: calc(7 * var(--unit)) 0px calc(10 * var(--unit));
`;

const Border = styled.div`
  height: 2px;
  background: linear-gradient(
    90deg,
    #ff86e1 0%,
    #78c2ff 25%,
    #ff86e1 50%,
    #78c2ff 75%,
    #ff86e1 100%
  );
`;

const RewardsContent = styled(Vertical)`
  width: 100%;
  padding: var(--double-unit) var(--quadruple-unit);
  background: linear-gradient(
      0deg,
      rgba(255, 134, 225, 0.1) 0%,
      rgba(255, 134, 225, 0.1) 100%
    ),
    rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(2px);
  align-items: flex-end;
`;

const StyledBezel = styled(Bezel)`
  padding: var(--intermediate-unit) var(--triple-unit);
  width: 100%;
`;

const PhoneImg = styled.img`
  width: 200px;
  max-width: 54%;
  position: absolute;
  bottom: 0;
  left: 0;
`;

const SideColor = styled.div`
  padding: 0 2px;
  background: linear-gradient(
    82deg,
    #cc8cd6 -11.67%,
    #be92d4 32.51%,
    #a28bd7 54.72%,
    #98ddf4 100%
  );
`;

const SideText = styled.div`
  width: var(--intermediate-unit);
  height: 100%;
  color: var(--text-color);
  background: url(${sideSvg}) repeat-y center / contain;
`;

const ESTIMATED_CASH_PRIZE = 1000;

export const SeriesWelcomeRewardsContent = () => {
  return (
    <Content center gap={1.5}>
      <RewardsWrapper gap={0}>
        <SideColor style={{ right: 0 } as CSSProperties}>
          <SideText className="reversedAnimation" />
        </SideColor>
        <RewardsBanner>
          <Border />
          <RewardsContent gap={1} center>
            <BrandS uppercase>
              <FormattedMessage
                id="onboarding.series.welcome.reward.cashRewardsUpTo"
                defaultMessage="Cash rewards up to"
              />
            </BrandS>
            <BrandXL
              className="text-gradient-color"
              style={
                {
                  '--text-gradient-color': 'var(--c-gradient-gold)',
                } as CSSProperties
              }
            >
              <FormattedNumber
                style="currency"
                currency="USD"
                value={ESTIMATED_CASH_PRIZE}
                currencyDisplay="narrowSymbol"
                maximumFractionDigits={0}
              />
              *
            </BrandXL>
          </RewardsContent>
          <Border />
          <PhoneImg src={phone} alt="" width={140} />
        </RewardsBanner>
        <SideColor style={{ right: 0 } as CSSProperties}>
          <SideText className="reversedAnimation" />
        </SideColor>
      </RewardsWrapper>
      <StyledBezel
        borderColor="var(--c-nd-150)"
        background={`conic-gradient(from 90deg at 4px 4px, var(--c-nd-150) 25%, rgba(0, 0, 0, 0.7) 0) 0 0/5px 5px`}
      >
        <Horizontal gap={2}>
          <img src={cards} alt="" width={80} />
          <Vertical>
            <BrandM>
              <FormattedMessage
                id="onboarding.series.welcome.reward.unpackRare"
                defaultMessage="Unpack rare"
              />
            </BrandM>
            <BrandM color="var(--c-score-high)">
              <FormattedMessage
                id="onboarding.series.welcome.reward.specialEditions"
                defaultMessage="Special editions"
              />
            </BrandM>
          </Vertical>
        </Horizontal>
      </StyledBezel>
      <BodyS color="var(--c-nd-700)" className="text-center">
        <FormattedMessage
          id="onboarding.series.welcome.reward.disclaimer"
          defaultMessage="*{amount} USD is the maximum reward for completing all Levels. Rewards may be claimed in ETH or fiat."
          values={{ amount: ESTIMATED_CASH_PRIZE }}
        />
      </BodyS>
    </Content>
  );
};
